import { cn } from "@/components/podkit/lib/cn";
import { Tooltip } from "@/components/Tooltip";
import type { PlainRunner } from "@/queries/runner-queries";
import { RunnerPhase } from "gitpod-next-api/gitpod/v1/runner_pb";
import { type FC, useMemo } from "react";

export const RunnerPhaseTag: FC<{ runner: PlainRunner; labelOverrides?: Partial<Record<RunnerPhase, string>> }> = ({
    runner,
    labelOverrides,
}) => {
    const { fgColor, bgColor, pulse, name, tooltip } = useMemo(() => {
        const actual = runner.status?.phase;
        const message = runner.status?.message || "";
        const desired = runner.spec?.desiredPhase;

        switch (desired) {
            case RunnerPhase.DELETED:
                return {
                    bgColor: "bg-surface-negative/10",
                    fgColor: "content-negative",
                    name: labelOverrides?.[RunnerPhase.DELETED] ?? "Pending deletion",
                    pulse: false,
                };
        }

        switch (actual) {
            case RunnerPhase.ACTIVE:
                return {
                    bgColor: "bg-surface-green/40",
                    fgColor: "text-content-positive",
                    name: labelOverrides?.[RunnerPhase.ACTIVE] ?? "Online",
                    pulse: false,
                };
            case RunnerPhase.DEGRADED:
                return {
                    bgColor: "bg-surface-negative/10",
                    fgColor: "content-negative",
                    name: labelOverrides?.[RunnerPhase.DEGRADED] ?? "Active (Degraded)",
                    tooltip: message,
                    pulse: false,
                };
            case RunnerPhase.CREATED:
                return {
                    bgColor: "bg-surface-orange/40",
                    fgColor: "text-content-orange",
                    name: labelOverrides?.[RunnerPhase.CREATED] ?? "Waiting to connect",
                    pulse: false,
                };
            case RunnerPhase.DELETING:
                return {
                    bgColor: "bg-surface-negative/10",
                    fgColor: "content-negative",
                    name: labelOverrides?.[RunnerPhase.DELETING] ?? "Pending deletion",
                    pulse: false,
                };
            case RunnerPhase.INACTIVE:
                return {
                    bgColor: "bg-surface-negative/10",
                    fgColor: "content-negative",
                    name: labelOverrides?.[RunnerPhase.INACTIVE] ?? "Offline",
                    pulse: false,
                };
            case RunnerPhase.DELETED:
                return {
                    bgColor: "bg-surface-primary",
                    fgColor: "text-content-secondary",
                    name: labelOverrides?.[RunnerPhase.DELETED] ?? "Deleted",
                    pulse: false,
                };
            default:
                return {
                    fgColor: "",
                    bgColor: "",
                    name: "Unknown",
                    pulse: false,
                };
        }
    }, [runner, labelOverrides]);

    return (
        <div
            className={cn(
                bgColor,
                "inline-flex flex-row items-center rounded-[20px] border-[0.5px] border-border-base px-2 py-1",
            )}
        >
            <Tooltip content={tooltip}>
                <div className={cn("text-sm font-bold", pulse ? "animate-pulse" : "animate-none")}>
                    <div className={fgColor}>{name}</div>
                </div>
            </Tooltip>
        </div>
    );
};
